var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useEffect } from "react";
import { useApolloClient } from "@apollo/client";
import { useListContext, useNotify } from "react-admin";
import { useTranslation } from "react-i18next";
import { FIND_APPLICATIONS_BY_SUPPORT_REFERENCE, } from "@/queries/FindApplicationsBySupportReference";
import { IPermissions } from "@/types/keycloak";
import { SUPPORT_REFERENCE_FILTER, normalizeSupportReference, } from "./supportReference";
/**
 * How long the search must stay the same before a reference is looked up: the
 * server hashes the event's applications for each lookup, so not one per
 * keystroke.
 */
export var SUPPORT_REFERENCE_DEBOUNCE_MS = 400;
/**
 * When the queue's search reads as a support reference (the one the pending
 * page gives the voter), asks the server for the applications it belongs to
 * within the queue's scope (`scope`: the event and its label) and adds them
 * to the search's matches. The list then reads them with the viewer's own
 * permissions, so the search shows nothing the queue wouldn't. It asks once
 * the search has stayed the same for [`SUPPORT_REFERENCE_DEBOUNCE_MS`].
 */
export var useSupportReferenceSearch = function (scope) {
    var _a;
    var _b = useListContext(), filterValues = _b.filterValues, setFilters = _b.setFilters;
    var client = useApolloClient();
    var notify = useNotify();
    var t = useTranslation().t;
    var search = typeof (filterValues === null || filterValues === void 0 ? void 0 : filterValues.q) === "string" ? filterValues.q : "";
    var reference = normalizeSupportReference(search);
    var resolved = (_a = filterValues === null || filterValues === void 0 ? void 0 : filterValues[SUPPORT_REFERENCE_FILTER]) === null || _a === void 0 ? void 0 : _a.reference;
    var electionEventId = typeof scope.election_event_id === "string" ? scope.election_event_id : undefined;
    var permissionLabel = typeof scope.permission_label === "string" ? scope.permission_label : undefined;
    useEffect(function () {
        if (!reference || resolved === reference || !electionEventId)
            return;
        var cancelled = false;
        var lookUp = function () {
            return client
                .query({
                query: FIND_APPLICATIONS_BY_SUPPORT_REFERENCE,
                variables: { electionEventId: electionEventId, reference: reference, permissionLabel: permissionLabel },
                fetchPolicy: "network-only",
                context: { headers: { "x-hasura-role": IPermissions.APPLICATION_READ } },
            })
                .then(function (_a) {
                var _b;
                var _c, _d;
                var data = _a.data;
                if (cancelled)
                    return;
                var match = {
                    reference: reference,
                    ids: (_d = (_c = data === null || data === void 0 ? void 0 : data.find_applications_by_support_reference) === null || _c === void 0 ? void 0 : _c.application_ids) !== null && _d !== void 0 ? _d : [],
                };
                setFilters(__assign(__assign({}, filterValues), (_b = {}, _b[SUPPORT_REFERENCE_FILTER] = match, _b)), {});
            })
                .catch(function () {
                if (!cancelled) {
                    notify(t("approvalsScreen.list.referenceError"), { type: "error" });
                }
            });
        };
        var timer = setTimeout(lookUp, SUPPORT_REFERENCE_DEBOUNCE_MS);
        return function () {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [reference, resolved, electionEventId, permissionLabel]);
};
